import React, { useState } from "react";
import { Card, DatePicker, Space, Button, Table, Typography, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import {
  useInventoryReportsServiceGetCustomerSalesSummary,
  useInventoryReportsServiceDownloadCustomerSalesSummary,
} from "api/queries";
const { Title } = Typography;

const SalesByCustomerReport = () => {
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));
  const [periodFilter, setPeriodFilter] = useState("This Month");

  const { data, isLoading, isError } =
    useInventoryReportsServiceGetCustomerSalesSummary({
      startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    });

  const { mutate: getDownloadUrl, isPending } =
    useInventoryReportsServiceDownloadCustomerSalesSummary();

  const customerSalesData =
    data?.customers?.map((item, idx: number) => ({
      key: item.phone || idx,
      customerName: item.customerName,
      customerPhone: item.phone,
      email: item.email,
      totalPurchases: item.totalPurchases,
      averageInvoiceValue: item.averageInvoiceValue,
      lastPurchaseDate: item.lastPurchaseDate,
      lastPurchaseAmount: item.lastPurchaseAmount,
      invoiceCount: item.invoiceCount,
    })) || [];

  const columns = [
    { title: "Customer Name", dataIndex: "customerName", key: "customerName" },
    { title: "Phone", dataIndex: "customerPhone", key: "customerPhone" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Total Purchases",
      dataIndex: "totalPurchases",
      key: "totalPurchases",
      render: (value: number) => `${value?.toLocaleString()}`,
    },
    {
      title: "Average Invoice Value",
      dataIndex: "averageInvoiceValue",
      key: "averageInvoiceValue",
      render: (value: number) => `${value?.toLocaleString()}`,
    },
    {
      title: "Last Purchase Date",
      dataIndex: "lastPurchaseDate",
      key: "lastPurchaseDate",
    },
    {
      title: "Last Purchase Amount",
      dataIndex: "lastPurchaseAmount",
      key: "lastPurchaseAmount",
      render: (value: number) => `${value?.toLocaleString()}`,
    },
    {
      title: "Invoice Count",
      dataIndex: "invoiceCount",
      key: "invoiceCount",
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
  };

  const handleDownload = () => {
    getDownloadUrl(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: {
          success: boolean;
          message: string;
          downloadUrl?: string;
        }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "sales_by_customer_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Sales by Customer Report</Title>
          <Space>
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              <Select.Option value="Today">Today</Select.Option>
              <Select.Option value="This Week">This Week</Select.Option>
              <Select.Option value="This Month">This Month</Select.Option>
              <Select.Option value="This Year">This Year</Select.Option>
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={customerSalesData}
          pagination={{ pageSize: 10 }}
          loading={isLoading}
          scroll={{ x: "max-content" }}
        />
      </Space>
    </motion.div>
  );
};

export default SalesByCustomerReport;
